import React, { useState, useEffect } from 'react';
import MenuItemForm from './MenuItemForm';
import { API_ENDPOINTS } from '../config/apiRouting';
import { useScope } from '../contexts/ScopeContext';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiGatewayMigration';
import './MenuItemManagement.css';
import MenuIngestionTools from './MenuIngestion/MenuIngestionTools';
import DocumentUpload from './MenuIngestion/DocumentUpload';
import WebsiteScraping from './MenuIngestion/WebsiteScraping';
import { hasPermission } from '../utils/roleUtils';
import { useAuth } from '../contexts/AuthContext';

interface MenuItemManagementProps {
  user: any;
}

interface MenuItem {
  id?: string; // For backward compatibility
  menuItemId?: string; // Backend field
  name: string;
  description: string;
  category: string;
  subcategory: string;
  price?: number; // Frontend format
  basePrice?: number; // Backend format
  cost: number;
  isActive: boolean;
  isAvailable: boolean;
  canBeModifier: boolean;
  preparationTime: number;
  difficulty: string;
  allergenInfo: string[];
  tags: string[];
  imageUrl: string;
  // New image fields
  menuImage?: string;
  onlineImage?: string;
  qualityControlImages?: string[];
  // Digital signage video field
  productVideo?: string;
  enterpriseId: string;
  conceptId: string;
  franchiseeId: string;
  storeId: string;
  createdAt: string;
  updatedAt: string;
}

const MenuItemManagement: React.FC<MenuItemManagementProps> = ({ user }) => {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [enterprises, setEnterprises] = useState<any[]>([]);
  const [enterpriseCategories, setEnterpriseCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const { enterpriseId: scopeEnterpriseId } = useScope();
  const [selectedEnterprise, setSelectedEnterprise] = useState('');
  const [userPickedEnterprise, setUserPickedEnterprise] = useState(false);

  // Follow the header enterprise selector until the user picks locally.
  useEffect(() => {
    if (userPickedEnterprise || !scopeEnterpriseId) return;
    if (selectedEnterprise === scopeEnterpriseId) return;
    if (enterprises.length > 0 && !enterprises.some((e: any) => e.enterpriseId === scopeEnterpriseId)) return;
    setSelectedEnterprise(scopeEnterpriseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeEnterpriseId, userPickedEnterprise, enterprises]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeModal, setActiveModal] = useState<string | null>(null);
  
  // Debug activeModal changes
  useEffect(() => {
    console.log('activeModal changed to:', activeModal);
    console.log('Rendering - activeModal:', activeModal, 'selectedEnterprise:', selectedEnterprise);
  }, [activeModal, selectedEnterprise]);

  const { getIdToken, session } = useAuth();

  const isSystemAdmin = hasPermission(user, 'canManageMenuItems', session);
  
  // Debug user and permissions
  useEffect(() => {
    console.log('User data:', user);
    console.log('Session:', session);
    console.log('Is System Admin:', isSystemAdmin);
  }, [user, session, isSystemAdmin]);

  // Add class to body for specific styling - MUST be before any conditional returns
  useEffect(() => {
    document.body.classList.add('menu-items-page');
    return () => {
      document.body.classList.remove('menu-items-page');
    };
  }, []);

  useEffect(() => {
    console.log('MenuItemManagement component mounted, loading enterprises...');
    loadEnterprises();
  }, []);

  // Debug enterprises state changes
  useEffect(() => {
    console.log('Enterprises state changed:', enterprises);
    console.log('Enterprises count:', enterprises.length);
    console.log('Selected Enterprise:', selectedEnterprise);
  }, [enterprises, selectedEnterprise]);

  useEffect(() => {
    if (selectedEnterprise) {
      loadMenuItems();
      loadCategories();
    }
  }, [selectedEnterprise]);

  const loadEnterprises = async () => {
    try {
      
      const data = await apiGet('enterprises');
      console.log('✅ Enterprises loaded successfully:', data);
      
      // The API returns the array directly, not wrapped in a data property
      const enterprisesList = Array.isArray(data) ? data : (data.data || []);
      setEnterprises(enterprisesList);
      
      // Auto-select: prefer header scope if present in list, else first enterprise.
      if (enterprisesList.length > 0) {
        const preferred = scopeEnterpriseId && enterprisesList.some((e: any) => e.enterpriseId === scopeEnterpriseId)
          ? scopeEnterpriseId
          : enterprisesList[0].enterpriseId;
        setSelectedEnterprise(preferred);
      }
    } catch (error) {
      console.error('❌ Error loading enterprises:', error);
      setEnterprises([]);
      alert(`Failed to load enterprises: ${error instanceof Error ? error.message : 'Please check your connection and try again.'}`);
    }
  };

  const loadMenuItems = async () => {
    if (!selectedEnterprise) return;
    
    try {
      setLoading(true);
      
      const data = await apiGet('menuItems', selectedEnterprise);
      console.log('✅ Menu items loaded successfully:', data.data?.length, 'items');
      
      // Map backend field names to frontend expected names
      const mappedData = (data.data || []).map((item: any) => ({
        ...item,
        // Map backend 'active'/'available' to frontend 'isActive'/'isAvailable'
        isActive: item.isActive !== undefined ? item.isActive : item.active,
        isAvailable: item.isAvailable !== undefined ? item.isAvailable : item.available
      }));
      
      setMenuItems(mappedData);
    } catch (error) {
      console.error('❌ Error loading menu items:', error);
      setMenuItems([]);
      alert(`Failed to load menu items: ${error instanceof Error ? error.message : 'Please check your connection and try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    if (!selectedEnterprise) return;
    
    try {
      
      // Categories will use legacy Lambda URL since not in API Gateway yet
      const data = await apiGet('categories', selectedEnterprise);
      console.log('✅ Categories response:', data);
      console.log('✅ Categories loaded successfully:', data.data?.length || data.length || 0, 'categories');
      
      setEnterpriseCategories(data.data || data || []);
    } catch (error) {
      console.error('❌ Error loading categories:', error);
      setEnterpriseCategories([]);
    }
  };

  const handleCreateMenuItem = async (formData: any) => {
    try {
      // Map frontend form data to backend expected format
      const backendData = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        subcategory: formData.subcategory,
        basePrice: formData.price, // Backend expects basePrice, not price
        cost: formData.cost,
        enterpriseId: formData.enterpriseId,
        conceptId: formData.conceptId || null,
        franchiseeId: formData.franchiseeId || null,
        storeId: formData.storeId || null,
        allergenInfo: formData.allergenInfo,
        preparationTime: formData.preparationTime,
        difficulty: formData.difficulty,
        // Send both field name formats for compatibility
        isActive: formData.isActive,
        isAvailable: formData.isAvailable,
        active: formData.isActive,
        available: formData.isAvailable,
        canBeModifier: formData.canBeModifier,
        tags: formData.tags,
        imageUrl: formData.imageUrl,
        // Add new image fields
        menuImage: formData.menuImage,
        onlineImage: formData.onlineImage,
        qualityControlImages: formData.qualityControlImages,
        // Add product video field
        productVideo: formData.productVideo,
        // Add required backend fields
        internalMenuItemId: `MI-${Date.now()}`, // Generate internal ID
        posSequenceNumber: Math.floor(Math.random() * 10000) // Generate sequence number
      };

      console.log('Sending update to backend:', {
        isActive: backendData.isActive,
        isAvailable: backendData.isAvailable,
        name: backendData.name,
        url: `${API_ENDPOINTS.menuItems()}/${editingItem?.menuItemId || editingItem?.id}`,
        fullBackendData: backendData
      });

      const idToken = await getIdToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      
      const response = await fetch(API_ENDPOINTS.menuItems(), {
        method: 'POST',
        headers,
        body: JSON.stringify(backendData),
      });

      if (response.ok) {
        setShowForm(false);
        loadMenuItems();
      } else {
        const errorData = await response.json();
        alert(`Failed to create menu item: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating menu item:', error);
      alert('Error creating menu item');
    }
  };

  const handleUpdateMenuItem = async (formData: any) => {
    if (!editingItem) return;

    try {
      // Map frontend form data to backend expected format
      const backendData = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        subcategory: formData.subcategory,
        basePrice: formData.price, // Backend expects basePrice, not price
        cost: formData.cost,
        enterpriseId: formData.enterpriseId,
        conceptId: formData.conceptId || null,
        franchiseeId: formData.franchiseeId || null,
        storeId: formData.storeId || null,
        allergenInfo: formData.allergenInfo,
        preparationTime: formData.preparationTime,
        difficulty: formData.difficulty,
        // Send both field name formats for compatibility
        isActive: formData.isActive,
        isAvailable: formData.isAvailable,
        active: formData.isActive,
        available: formData.isAvailable,
        canBeModifier: formData.canBeModifier,
        tags: formData.tags,
        imageUrl: formData.imageUrl,
        // Add new image fields
        menuImage: formData.menuImage,
        onlineImage: formData.onlineImage,
        qualityControlImages: formData.qualityControlImages,
        // Add product video field
        productVideo: formData.productVideo
      };

      const idToken = await getIdToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      
      const menuItemId = editingItem.menuItemId || editingItem.id;
      if (!menuItemId) {
        console.error('No menu item ID found for update');
        return;
      }
      
      const response = await fetch(API_ENDPOINTS.menuItem(menuItemId), {
        method: 'PUT',
        headers,
        body: JSON.stringify(backendData),
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log('Menu item update successful. Response:', {
          isActive: responseData.data?.isActive,
          isAvailable: responseData.data?.isAvailable,
          name: responseData.data?.name,
          fullResponseData: responseData
        });
        setShowForm(false);
        setEditingItem(null);
        loadMenuItems();
      } else {
        const errorData = await response.json();
        console.error('Menu item update failed:', errorData);
        alert(`Failed to update menu item: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating menu item:', error);
      alert('Error updating menu item');
    }
  };

  const handleDeleteMenuItem = async (menuItemId: string) => {
    if (!window.confirm('Are you sure you want to delete this menu item?')) return;

    try {
      const idToken = await getIdToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      
      const response = await fetch(API_ENDPOINTS.menuItem(menuItemId), {
        method: 'DELETE',
        headers,
      });

      if (response.ok) {
        loadMenuItems();
      } else {
        alert('Failed to delete menu item');
      }
    } catch (error) {
      console.error('Error deleting menu item:', error);
      alert('Error deleting menu item');
    }
  };

  const handleEdit = (menuItem: MenuItem) => {
    setEditingItem(menuItem);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingItem(null);
  };

  const handleFormSubmit = (formData: any) => {
    if (editingItem) {
      handleUpdateMenuItem(formData);
    } else {
      handleCreateMenuItem(formData);
    }
  };

  // Filter menu items
  const filteredMenuItems = menuItems.filter(item => {
    const itemName = item.name || '';
    const itemDescription = item.description || '';
    const matchesSearch = itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         itemDescription.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || item.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && item.isActive !== false) ||
                         (statusFilter === 'inactive' && item.isActive === false) ||
                         (statusFilter === 'available' && item.isAvailable !== false) ||
                         (statusFilter === 'unavailable' && item.isAvailable === false);
    
    return matchesSearch && matchesCategory && matchesStatus;
  });
  
  // Debug logging
  console.log('Filter debug:', {
    menuItemsCount: menuItems.length,
    filteredCount: filteredMenuItems.length,
    filters: { searchTerm, categoryFilter, statusFilter },
    sampleItem: menuItems[0]
  });

  // Use enterprise categories if available, otherwise extract from menu items
  const enterpriseCategoryNames = Array.isArray(enterpriseCategories) ? enterpriseCategories.map(cat => cat.name) : [];
  const extractedCategories = Array.from(new Set(menuItems.map((item: MenuItem) => item.category))).sort();
  const categories = enterpriseCategoryNames.length > 0 ? enterpriseCategoryNames : extractedCategories;

  if (loading && !selectedEnterprise) {
    return (
      <div className="menu-management" style={{
        minHeight: '100vh',
        maxHeight: 'none',
        height: 'auto',
        overflow: 'visible',
        display: 'block'
      }}>
        <div className="loading-state">
          <div className="loading-skeleton"></div>
          <p>Loading menu items...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="menu-management" style={{
        minHeight: '100vh',
        maxHeight: 'none',
        height: 'auto',
        overflow: 'visible',
        display: 'block'
      }}>
      {/* Menu Ingestion Banner */}
      <div className="ingestion-banner">
        <div className="banner-content">
          <span className="banner-icon">[NEW]</span>
          <div className="banner-text">
            <strong>NEW:</strong> AI-Powered Menu Ingestion Tools! 
            Import menu items from PDFs, images, websites, or Excel/CSV files automatically.
          </div>
          <button 
            onClick={() => {
              console.log('Banner button clicked, selectedEnterprise:', selectedEnterprise);
              if (!selectedEnterprise) {
                alert('Please select an enterprise first.');
                return;
              }
              console.log('Setting activeModal to ingestion-tools');
              setActiveModal('ingestion-tools');
            }} 
            className="banner-button"
          >
            Try Now
          </button>
        </div>
      </div>
      
      {/* Header */}
      <div className="menu-header">
        <div>
          <p>Create and manage menu items for your restaurants</p>
        </div>
        <div className="header-buttons">
          <button 
            onClick={() => {
              console.log('Header button clicked, selectedEnterprise:', selectedEnterprise);
              if (!selectedEnterprise) {
                alert('Please select an enterprise first.');
                return;
              }
              console.log('Setting activeModal to ingestion-tools');
              setActiveModal('ingestion-tools');
            }} 
            className="btn-ingestion"
            title="AI-powered menu import from documents, websites, and Excel/CSV files"
          >
            Menu Ingestion Tools
          </button>
          <button onClick={() => setShowForm(true)} className="btn-primary">
            Add Menu Item
          </button>
        </div>
      </div>

      {/* Toolbar with Filters — enterprise selection comes from the header strip. */}
      <div className="menu-toolbar">
        {selectedEnterprise && (
          <>
            <div className="filter-group">
              <label>Search</label>
              <input
                type="text"
                placeholder="Search menu items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-professional"
              />
            </div>

            <div className="filter-group">
              <label>Category</label>
              <select 
                value={categoryFilter} 
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="select-professional"
              >
                <option value="">All categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Status</label>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="select-professional"
              >
                <option value="all">All items</option>
                <option value="active">Active only</option>
                <option value="inactive">Inactive only</option>
                <option value="available">Available only</option>
                <option value="unavailable">Unavailable only</option>
              </select>
            </div>

            <div className="filter-group">
              <label>&nbsp;</label>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setCategoryFilter('');
                  setStatusFilter('all');
                }}
                className="btn-clear"
              >
                Clear Filters
              </button>
            </div>
          </>
        )}
      </div>

      {/* Content Area */}
      <div className="menu-content">
        {selectedEnterprise && (
          <>
            {/* Menu Items Grid */}
            {loading ? (
              <div className="loading-state">
                <div className="loading-skeleton"></div>
                <p>Loading menu items...</p>
              </div>
            ) : filteredMenuItems.length === 0 ? (
              <div className="empty-state">
                <h3>
                  {menuItems.length === 0 ? 'No menu items found' : 'No items match your filters'}
                </h3>
                {menuItems.length === 0 && (
                  <button onClick={() => setShowForm(true)} className="btn-primary">
                    Create First Menu Item
                  </button>
                )}
              </div>
            ) : (
              <div className="menu-items-grid">
                {filteredMenuItems.map(menuItem => (
                  <div key={menuItem.menuItemId || menuItem.id} className="menu-item-card">
                    <img
                      src={menuItem.imageUrl || menuItem.menuImage || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjE1MCIgeT0iMTA1IiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM2QjcyODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPk5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4K'}
                      alt={menuItem.name}
                      className="menu-item-image"
                    />
                    <div className="menu-item-content">
                      <h3 className="menu-item-title">{menuItem.name || 'Unnamed Item'}</h3>
                      <p className="menu-item-description">{menuItem.description || 'No description'}</p>

                      <div className="menu-item-price-row">
                        <span className="badge badge-category">{menuItem.category || 'Uncategorized'}</span>
                        <span className="menu-item-price">${(menuItem.basePrice || menuItem.price || 0).toFixed(2)}</span>
                      </div>

                      <div className="menu-item-badges">
                        {!menuItem.isActive && <span className="badge badge-inactive">Inactive</span>}
                        {!menuItem.isAvailable && <span className="badge badge-unavailable">Unavailable</span>}
                        {menuItem.canBeModifier && <span className="badge badge-modifier">Modifier</span>}
                        {menuItem.difficulty && <span className="badge badge-category">{menuItem.difficulty}</span>}
                      </div>

                      {menuItem.allergenInfo && menuItem.allergenInfo.length > 0 && (
                        <div className="menu-item-allergens">
                          Allergens: {menuItem.allergenInfo.join(', ')}
                        </div>
                      )}

                      <div className="menu-item-footer">
                        <div className="menu-item-time">
                          {menuItem.preparationTime > 0 && `${menuItem.preparationTime} min`}
                        </div>
                        
                        <div className="menu-item-actions">
                          <button
                            onClick={() => handleEdit(menuItem)}
                            className="btn-small btn-secondary"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteMenuItem(menuItem.menuItemId || menuItem.id || '')}
                            className="btn-small btn-secondary"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Menu Item Form Modal */}
      {showForm && (
        <MenuItemForm
          onSubmit={handleFormSubmit}
          onCancel={handleCancelForm}
          initialData={editingItem}
          enterprises={enterprises}
        />
      )}

    </div>

    {/* Menu Ingestion Tools Modal */}
    {activeModal === 'ingestion-tools' && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        backdropFilter: 'blur(4px)'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          width: '95vw',
          maxWidth: '1200px',
          height: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          position: 'relative'
        }}>
          {/* Header with close button */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.5rem',
            borderBottom: '1px solid #e5e7eb',
            position: 'sticky',
            top: 0,
            background: 'white',
            zIndex: 10
          }}>
            <h2 style={{margin: 0, fontSize: '1.5rem', fontWeight: '600'}}>
              Menu Ingestion Tools
            </h2>
            <button 
              onClick={() => setActiveModal(null)}
              style={{
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '0.5rem 1rem',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Close
            </button>
          </div>
          
          {/* Content */}
          <div style={{padding: '1.5rem'}}>
            <MenuIngestionTools
              enterpriseId={selectedEnterprise}
              onClose={() => setActiveModal(null)}
            />
          </div>
        </div>
      </div>
    )}

    {activeModal === 'document-upload' && (
      <div className="modal-overlay" onClick={() => setActiveModal(null)} style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999
      }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <DocumentUpload
            enterpriseId={selectedEnterprise}
            onJobCreated={(jobId) => {
              console.log('Job created:', jobId);
              setActiveModal(null);
              // Refresh menu items after ingestion
              loadMenuItems();
            }}
            onClose={() => setActiveModal(null)}
          />
        </div>
      </div>
    )}

    {activeModal === 'website-scraping' && (
      <div className="modal-overlay" onClick={() => setActiveModal(null)} style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999
      }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <WebsiteScraping
            enterpriseId={selectedEnterprise}
            onJobCreated={(jobId) => {
              console.log('Job created:', jobId);
              setActiveModal(null);
              // Refresh menu items after ingestion
              loadMenuItems();
            }}
            onClose={() => setActiveModal(null)}
          />
        </div>
      </div>
    )}
    </>
  );
};

export default MenuItemManagement;